import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  OnInit,
  SecurityContext,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import * as fromSeriousSystem from '@serious-system';
import {
  effectiveOutcome,
  type OutcomeEdits,
  type OutcomeEnvelope,
} from '@shared/outcome';
import type { Editor } from '@tiptap/core';
import { MarkdownComponent, MarkdownPipe } from 'ngx-markdown';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import * as fromGenerated from '../../../../../_generated';
import { AnalyticsService } from '../../../../../analytics';
import * as fromFeatureFlags from '../../../../../feature-flags';
import { makeLinksInert, withInertLinks } from '../../../../../shared/markdown';
import { getFilenameFromContentDisposition } from '../../../../shared';
import { ChatWithMessagesActions } from '../../../../store/chats.actions';
import { reportsExportFailure } from '../quick-actions/message-quick-actions-export.component';
import { writeToClipboard } from './outcome-clipboard';

type CopyTarget = 'body' | 'subject';
type ExportFormat = 'docx' | 'pdf';

/** SK-155: each export's name on its chip, and its error, as the answer's Export menu says it. */
const EXPORTS = {
  docx: { name: 'Word', errorKey: 'CHAT.DROPDOWN_MENU.EXPORT_TO_DOCX_ERROR' },
  pdf: { name: 'PDF', errorKey: 'CHAT.DROPDOWN_MENU.EXPORT_TO_PDF_ERROR' },
} as const satisfies Record<ExportFormat, { name: string; errorKey: string }>;

/** The outcome a block shows, and where its edits are saved. */
interface EditTarget {
  readonly chatUuid: string | null;
  readonly messageId: number | null;
  readonly index: number;
  readonly envelope: OutcomeEnvelope;
  /** The outcome's original body. */
  readonly body: string;
}

const sameTarget = (a: EditTarget, b: EditTarget): boolean =>
  a.chatUuid === b.chatUuid &&
  a.messageId === b.messageId &&
  a.index === b.index &&
  a.body === b.body &&
  a.envelope === b.envelope;

/**
 * B5: a link in the editor never opens on click. Autolink is off because
 * Tiptap makes the link mark inclusive when it is on: text typed right after
 * a link would join it.
 */
const LINK_OPTIONS = { openOnClick: false, autolink: false } as const;

/**
 * B5: Tiptap, loaded with the first editable document rather than with the
 * app (about 500 kB, most of it ProseMirror).
 */
const loadTiptap = async () => {
  const [
    { Editor },
    { Link },
    { TaskItem, TaskList },
    { TableKit },
    { Markdown },
    { StarterKit },
  ] = await Promise.all([
    import('@tiptap/core'),
    import('@tiptap/extension-link'),
    import('@tiptap/extension-list'),
    import('@tiptap/extension-table'),
    import('@tiptap/markdown'),
    import('@tiptap/starter-kit'),
  ]);
  return {
    Editor,
    TableKit,
    Markdown,
    StarterKit,
    // The answer's task lists (SB-3281) survive an edit: without these
    // nodes, the first keystroke would rewrite `- [ ]` items as plain text.
    TaskList,
    TaskItem: TaskItem.configure({ nested: true }),
    /**
     * SK-135 KD-37: on a Teammate chat a link in the editor is drawn as
     * underlined text that never navigates. The mark is kept, so the saved
     * markdown keeps the link.
     */
    InertLink: Link.extend({
      renderHTML: () => ['span', { class: 'underline' }, 0],
    }).configure(LINK_OPTIONS),
  };
};

/** How long typing pauses before an edit is saved. */
const SAVE_DELAY_MS = 800;

/**
 * SK-135: a deliverable core-ai marked in the answer, drawn as SK-135's
 * Option B block — a header with its label, its tags and a Copy button, the
 * subject on its own row with its own copy icon, then the body.
 *
 * What it shows comes from the envelope's fields, never from its `kind`: a
 * kind it has never seen gets the same block. The one exception is the copy
 * toast's wording, "Translation copied" (`toastKey`). The envelope is model
 * output, so every string of it is interpolated, never bound as HTML; the body is
 * plain text kept as written, or markdown rendered like the answer's prose.
 *
 * `open` is the live view's block whose end marker has not arrived yet: it
 * fills as the text streams in, and copies or edits nothing until complete.
 *
 * B4: a plain body and the subject are edited in place; B5: a rich body too,
 * in a Tiptap editor that keeps headings, lists, tables and bold, and saves
 * markdown. An edit is saved after a pause in typing and on leaving the
 * field; "Edited · Restore original" shows while the text differs from the
 * original; Restore brings the original back. A failed save keeps the text and
 * says so. Without a chat (the playground) edits stay on the page.
 */
@Component({
  selector: 'squadbox-outcome-block',
  imports: [
    MarkdownComponent,
    TranslateModule,
    fromSeriousSystem.ButtonDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.SpinnerDirective,
    fromSeriousSystem.UseFileTypeIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  providers: [MarkdownPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      class="outcome-block rounded-xl border border-solid border-neutral-200 bg-white focus-within:border-primary-300"
      role="region"
      [attr.aria-label]="label()"
      [attr.aria-busy]="open() ? 'true' : null"
    >
      <div
        class="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b border-solid border-neutral-200 py-1.5 pr-2 pl-5"
      >
        <div
          class="typo-p3 flex min-w-0 flex-wrap items-center gap-2 text-neutral-500"
        >
          <span class="min-w-0 break-words">{{ label() }}</span>
          @for (tag of envelope().tags ?? []; track $index) {
            <span
              class="typo-caption rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-500"
              >{{ tag }}</span
            >
          }
        </div>
        @if (!open()) {
          <button
            type="button"
            sdButton
            variant="text"
            color="neutral"
            size="xs"
            (click)="copy('body')"
          >
            <svg
              [sdUseUIBasicIcon]="copied() === 'body' ? 'check' : 'copy'"
            ></svg>
            {{ copyBodyKey() | translate }}
          </button>
        }
      </div>

      @if (envelope().subject !== undefined) {
        <div
          class="flex items-start gap-1 border-b border-solid border-neutral-200"
        >
          <span class="typo-p2 flex-none pt-3 pl-5 text-neutral-500">{{
            'CHAT.OUTCOME.SUBJECT' | translate
          }}</span>
          @if (editable()) {
            <input
              #subjectField
              type="text"
              class="typo-p2 m-0 min-w-0 flex-1 border-0 bg-transparent py-3 pr-2 pl-1.5 font-semibold outline-none"
              [attr.aria-label]="'CHAT.OUTCOME.EDIT_SUBJECT' | translate"
              (input)="onSubjectInput(subjectField.value)"
              (keydown.enter)="$event.preventDefault(); subjectField.blur()"
              (blur)="saveNow()"
            />
          } @else {
            <p
              class="typo-p2 m-0 min-w-0 flex-1 py-3 pr-2 pl-1.5 font-semibold break-words"
              [textContent]="subject()"
            ></p>
          }
          @if (!open()) {
            <button
              type="button"
              class="mt-2 mr-2 flex-none"
              sdIconButton
              variant="icon"
              size="xs"
              color="neutral"
              [attr.aria-label]="'CHAT.OUTCOME.COPY_SUBJECT' | translate"
              (click)="copy('subject')"
            >
              <svg
                [sdUseUIBasicIcon]="copied() === 'subject' ? 'check' : 'copy'"
              ></svg>
            </button>
          }
        </div>
      }

      <div class="outcome-block__body px-5 py-4">
        @if (envelope().format === 'rich') {
          @if (editable() && editing()) {
            <!-- B5: the editor mounts here on the first focus or click; like
                 the textarea, it is filled, never bound. Hidden until it is
                 ready, while the rendered body below stays on screen. -->
            <div #richField [class.hidden]="!editorReady()"></div>
          }
          @if (!editorReady()) {
            <!-- On a Teammate chat the body stays hidden and inert until its
                 ready handler has made every link inert (SK-135 B2-T10). -->
            <markdown
              #rich
              [class.invisible]="inertLinks() && !richInert()"
              [class.cursor-text]="editable()"
              [attr.inert]="inertLinks() && !richInert() ? '' : null"
              [attr.tabindex]="editable() ? 0 : null"
              [attr.role]="editable() ? 'textbox' : null"
              [attr.aria-label]="
                editable() ? ('CHAT.OUTCOME.EDIT_BODY' | translate) : null
              "
              [data]="text()"
              (ready)="onRichReady(rich)"
              (pointerdown)="startEditing($event)"
              (focusin)="startEditing()"
            />
          }
        } @else if (editable()) {
          <!-- A real textarea: pasting keeps the text only, and it grows
               with its content so the block reads like the answer. Its value
               is set once, never bound: rebinding it on every keystroke can
               move the cursor in Safari and Firefox. -->
          <textarea
            #bodyField
            class="typo-p2 m-0 block w-full resize-none overflow-hidden border-0 bg-transparent p-0 break-words outline-none"
            rows="1"
            [attr.aria-label]="'CHAT.OUTCOME.EDIT_BODY' | translate"
            (input)="onBodyInput(bodyField.value); fit(bodyField)"
            (blur)="saveNow()"
          ></textarea>
        } @else {
          <!-- textContent, not interpolation: the template's own whitespace
               would show in a pre-wrap body. -->
          <p
            class="typo-p2 m-0 break-words whitespace-pre-wrap"
            [textContent]="text()"
          ></p>
        }
      </div>

      @if (exportFormats().length > 0) {
        <!-- SK-155: a document's Export row, Word and PDF as the answer's
             Export menu offers them; the file holds this block only. -->
        <div
          class="flex flex-wrap items-center gap-2 rounded-b-xl border-t border-solid border-neutral-200 bg-neutral-50 py-2.5 pr-3 pl-5"
        >
          <span class="typo-p3 mr-1 text-neutral-500">{{
            'CHAT.OUTCOME.EXPORT' | translate
          }}</span>
          @for (format of exportFormats(); track format) {
            <button
              type="button"
              class="bg-white"
              sdButton
              variant="outline"
              color="neutral"
              size="xs"
              [disabled]="exporting() !== null"
              (click)="onExport(format)"
            >
              @if (exporting() === format) {
                <div sdSpinner size="sm" color="primary"></div>
                {{ 'CHAT.OUTCOME.EXPORTING' | translate }}
              } @else if (exported() === format) {
                <svg sdUseUIBasicIcon="check"></svg>
                {{
                  'CHAT.OUTCOME.EXPORTED'
                    | translate: { format: exports[format].name }
                }}
              } @else {
                <svg
                  [sdUseFileTypeIcon]="format === 'docx' ? 'word' : 'pdf'"
                ></svg>
                {{ exports[format].name }}
              }
            </button>
          }
        </div>
      }
    </section>
    @if (edited()) {
      <p class="typo-p3 m-0 flex items-center gap-1 pt-1.5 text-neutral-500">
        {{ 'CHAT.OUTCOME.EDITED' | translate }} ·
        <button
          type="button"
          sdButton
          variant="text"
          color="primary"
          size="xs"
          (click)="restore()"
        >
          {{ 'CHAT.OUTCOME.RESTORE' | translate }}
        </button>
      </p>
    }
    <!-- The result of a copy, a save or a restore, for a screen reader. -->
    <span class="sr-only" role="status">{{ announcement() }}</span>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .outcome-block__body markdown > *:first-child {
        margin-top: 0;
      }
    `,
  ],
})
export class OutcomeBlockComponent implements OnInit {
  public readonly envelope = input.required<OutcomeEnvelope>();
  /** The outcome's body as core-ai wrote it. */
  public readonly body = input.required<string>();
  /** The live view's block whose end marker has not arrived yet. */
  public readonly open = input(false);
  /**
   * The live view's blocks are never edited: the saved reply replaces them
   * moments later, and an edit made there would be lost.
   */
  public readonly readOnly = input(false);
  /** The chat it belongs to, for analytics; none in the playground. */
  public readonly chatId = input<number | null>(null);
  /** Where its edits are saved; without both, edits stay on the page. */
  public readonly chatUuid = input<string | null>(null);
  public readonly messageId = input<number | null>(null);
  /** Its position among the answer's outcomes: the key of its edits. */
  public readonly index = input(0);
  /** The message's saved edits (`ChatMessage.outcomeEdits`). */
  public readonly edits = input<OutcomeEdits | null | undefined>(null);
  /**
   * SK-135 B2-T10 (KD-37): on a Teammate chat no link in the block navigates,
   * on screen or in the copied HTML.
   */
  public readonly inertLinks = input(false);
  /** The rich body's links were made inert after its last render. */
  protected readonly richInert = signal(false);

  private readonly translate = inject(TranslateService);
  private readonly toastr = inject(ToastrService);
  private readonly markdownPipe = inject(MarkdownPipe);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly analytics = inject(AnalyticsService);
  private readonly store = inject(Store);
  private readonly chatsService = inject(fromGenerated.ChatsService);

  private readonly bodyField =
    viewChild<ElementRef<HTMLTextAreaElement>>('bodyField');
  private readonly subjectField =
    viewChild<ElementRef<HTMLInputElement>>('subjectField');
  private readonly richField =
    viewChild<ElementRef<HTMLDivElement>>('richField');

  /**
   * B5: a document's editor mounts on its first focus or click, not when it
   * is shown: a chat holding many documents keeps one editor per document
   * the user opened, not one per document on screen.
   */
  protected readonly editing = signal(false);
  protected readonly editorReady = signal(false);
  /** Where the click that opened the editor landed, or its start for a focus. */
  private focusAt: { left: number; top: number } | 'start' | undefined;
  private readonly injector = inject(Injector);

  /** B5: the rich body's editor, once the user opened it. */
  private editor: Editor | undefined;
  private editorHost: HTMLElement | undefined;
  private editorInertLinks = false;
  private destroyed = false;
  /**
   * What the editor was filled with, and its markdown right after: typing
   * that brings the editor back to it is no edit, whatever markdown the editor
   * would write for it.
   */
  private filledText = '';
  private filledMarkdown = '';

  protected readonly copied = signal<CopyTarget | null>(null);
  protected readonly announcement = signal('');

  /** What the saved edits make of this outcome: the edit, or the original. */
  private readonly shown = computed(() =>
    effectiveOutcome(
      {
        kind: 'outcome',
        index: this.index(),
        envelope: this.envelope(),
        body: this.body(),
      },
      this.edits() ?? null,
    ),
  );

  /** The text on screen: the saved version, then whatever the user typed. */
  protected readonly text = linkedSignal(() => this.shown().body);
  protected readonly subject = linkedSignal(() => this.shown().subject ?? '');

  /** A complete outcome outside the live view: edited in place, plain or rich. */
  protected readonly editable = computed(
    () => !this.open() && !this.readOnly(),
  );

  private readonly ffQuickActions = this.store.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectQuickActions,
  );

  /**
   * SK-155: the Export row of a complete document saved in a chat, with the
   * formats this deployment exports an answer to.
   */
  protected readonly exportFormats = computed((): ExportFormat[] => {
    if (
      this.open() ||
      this.readOnly() ||
      this.envelope().format !== 'rich' ||
      this.chatUuid() === null ||
      this.messageId() === null
    ) {
      return [];
    }
    const enabled = this.ffQuickActions()?.export;
    return (['docx', 'pdf'] as const).filter((format) => enabled?.[format]);
  });
  protected readonly exports = EXPORTS;
  protected readonly exporting = signal<ExportFormat | null>(null);
  protected readonly exported = signal<ExportFormat | null>(null);

  /** "Edited · Restore original" shows while the text differs from the original. */
  protected readonly edited = computed(
    () =>
      this.text() !== this.body() ||
      this.subject() !== (this.envelope().subject ?? ''),
  );

  /** The model's label, or the translated fallback when it wrote none. */
  protected readonly label = computed(
    () =>
      this.envelope().label ??
      String(this.translate.instant('CHAT.OUTCOME.FALLBACK_LABEL')),
  );

  protected readonly copyBodyKey = computed(() =>
    this.envelope().subject ? 'CHAT.OUTCOME.COPY_MESSAGE' : 'CHAT.OUTCOME.COPY',
  );

  /** The outcome on screen now, and where its edits go. */
  private readonly target = computed((): EditTarget => ({
    chatUuid: this.chatUuid(),
    messageId: this.messageId(),
    index: this.index(),
    envelope: this.envelope(),
    body: this.body(),
  }));
  /** The outcome the fields hold text for. */
  private filledFor: EditTarget | undefined;

  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private dirty = false;
  /**
   * The last write sent, so the next one waits for it: two saves of one
   * outcome reach the API (and its row lock) in the order they were made.
   */
  private writing: Promise<boolean> = Promise.resolve(true);
  private editTracked = false;

  constructor() {
    // The chat view reuses a block when another conversation opens (its
    // segments are tracked by position). The fields are never bound, so they
    // are refilled here; a pending edit is saved to the outcome it was typed
    // in first, never to the one now shown.
    effect(() => {
      const target = this.target();
      untracked(() => this.showTarget(target));
    });
    effect(() => {
      const host = this.richField()?.nativeElement;
      const inertLinks = this.inertLinks();
      untracked(() => void this.mountEditor(host, inertLinks));
    });
    inject(DestroyRef).onDestroy(() => {
      // Leaving the chat mid-typing still saves what was typed.
      if (this.dirty && this.filledFor) {
        void this.save(this.filledFor);
      }
      this.destroyed = true;
      this.editor?.destroy();
    });
  }

  private showTarget(target: EditTarget): void {
    const previous = this.filledFor;
    if (previous && sameTarget(previous, target)) {
      return;
    }
    if (previous && this.dirty) {
      clearTimeout(this.saveTimer);
      void this.save(previous);
    }
    this.editTracked = false;
    this.copied.set(null);
    this.announcement.set('');
    this.filledFor = target;
    // After the view has rendered this outcome's fields.
    queueMicrotask(() => this.fillFields());
  }

  ngOnInit(): void {
    const chatId = this.chatId();
    if (!this.open() && chatId !== null) {
      this.analytics
        .sendOutcomeShownEvent({
          chatId,
          kind: this.envelope().kind,
          format: this.envelope().format,
        })
        .subscribe();
    }
  }

  /** Puts the text on screen into the fields, once, and sizes the body. */
  private fillFields(): void {
    const body = this.bodyField()?.nativeElement;
    if (body) {
      body.value = this.text();
      this.fit(body);
    }
    const subject = this.subjectField()?.nativeElement;
    if (subject) {
      subject.value = this.subject();
    }
    if (this.editor) {
      this.editor.commands.setContent(this.text(), {
        contentType: 'markdown',
        emitUpdate: false,
      });
      this.filledText = this.text();
      this.filledMarkdown = this.editor.getMarkdown();
    }
  }

  /** B5: one editor per rich body on screen, filled like the other fields. */
  private async mountEditor(
    host: HTMLElement | undefined,
    inertLinks: boolean,
  ): Promise<void> {
    if (host === this.editorHost && inertLinks === this.editorInertLinks) {
      return;
    }
    this.editor?.destroy();
    this.editor = undefined;
    this.editorReady.set(false);
    this.editorHost = host;
    this.editorInertLinks = inertLinks;
    if (!host) {
      return;
    }
    const {
      Editor,
      InertLink,
      Markdown,
      StarterKit,
      TableKit,
      TaskItem,
      TaskList,
    } = await loadTiptap();
    // Another outcome, or no editor at all, while Tiptap was loading.
    if (
      host !== this.editorHost ||
      inertLinks !== this.editorInertLinks ||
      this.destroyed
    ) {
      return;
    }
    this.editor = new Editor({
      element: host,
      extensions: [
        // Markdown is what is saved: no mark it cannot write (underline), and
        // no empty paragraph appended after the last block.
        StarterKit.configure({
          underline: false,
          trailingNode: false,
          link: inertLinks ? false : LINK_OPTIONS,
        }),
        ...(inertLinks ? [InertLink] : []),
        TableKit,
        TaskList,
        TaskItem,
        Markdown,
      ],
      content: this.text(),
      contentType: 'markdown',
      editorProps: {
        attributes: {
          class: 'markdown-prose outline-none',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': String(
            this.translate.instant('CHAT.OUTCOME.EDIT_BODY'),
          ),
        },
      },
      onUpdate: () => this.onRichInput(),
      onBlur: () => this.saveNow(),
    });
    this.filledText = this.text();
    this.filledMarkdown = this.editor.getMarkdown();
    this.editorReady.set(true);
    const at = this.focusAt;
    this.focusAt = undefined;
    if (at !== undefined) {
      afterNextRender(() => this.focusEditor(at), { injector: this.injector });
    }
  }

  /** B5: the first focus or click in a document opens its editor. */
  protected startEditing(event?: PointerEvent): void {
    if (!this.editable() || this.editing()) {
      return;
    }
    this.focusAt = event
      ? { left: event.clientX, top: event.clientY }
      : 'start';
    this.editing.set(true);
  }

  /** The caret goes where the user clicked: the editor draws the body as the rendered view did. */
  private focusEditor(at: { left: number; top: number } | 'start'): void {
    const editor = this.editor;
    if (!editor) {
      return;
    }
    const position =
      at === 'start' ? undefined : editor.view.posAtCoords(at)?.pos;
    editor.commands.focus(position ?? 'start', { scrollIntoView: false });
  }

  /** B5: the rich body as typed, or the text it was filled with when unchanged. */
  private richBody(): string | undefined {
    const markdown = this.editor?.getMarkdown();
    if (markdown === undefined) {
      return undefined;
    }
    return markdown === this.filledMarkdown ? this.filledText : markdown;
  }

  private onRichInput(): void {
    this.text.set(this.richBody() ?? this.text());
    this.scheduleSave();
  }

  protected onBodyInput(value: string): void {
    this.text.set(value);
    this.scheduleSave();
  }

  protected onSubjectInput(value: string): void {
    this.subject.set(value);
    this.scheduleSave();
  }

  /** The textarea grows with its content, so the block reads like the answer. */
  protected fit(field: HTMLTextAreaElement): void {
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
  }

  protected saveNow(): void {
    if (this.dirty) {
      clearTimeout(this.saveTimer);
      void this.save(this.target());
    }
  }

  protected async restore(): Promise<void> {
    clearTimeout(this.saveTimer);
    this.dirty = false;
    this.text.set(this.body());
    this.subject.set(this.envelope().subject ?? '');
    this.fillFields();
    this.bodyField()?.nativeElement.focus();
    // Back in the editor, without scrolling the page to its cursor.
    this.editor?.commands.focus(null, { scrollIntoView: false });
    if (await this.write(null, this.target())) {
      this.announcement.set(
        String(this.translate.instant('CHAT.OUTCOME.RESTORED')),
      );
      this.track('restored');
    }
  }

  /**
   * ngx-markdown's `ready`: it writes the HTML and emits in one task, so this
   * runs before the browser can deliver a click.
   */
  protected onRichReady(rich: MarkdownComponent): void {
    if (!this.inertLinks()) {
      return;
    }
    makeLinksInert(rich.element.nativeElement);
    this.richInert.set(true);
  }

  protected async copy(target: CopyTarget): Promise<void> {
    const envelope = this.envelope();
    const plain = target === 'subject' ? this.subject() : this.text();
    // Rendered inside the clipboard write, never awaited before it (Safari).
    const html =
      target === 'body' && envelope.format === 'rich'
        ? this.markdownPipe.transform(plain).then((rendered) => {
            const safe =
              this.sanitizer.sanitize(SecurityContext.HTML, rendered) ?? '';
            return this.inertLinks() ? withInertLinks(safe) : safe;
          })
        : undefined;

    const written = await writeToClipboard(plain, html);
    const message = String(
      this.translate.instant(
        written ? this.toastKey(target) : 'CHAT.OUTCOME.TOAST.COPY_FAILED',
      ),
    );
    this.announcement.set(message);
    if (!written) {
      this.toastr.error(message);
      return;
    }
    this.toastr.success(message);
    this.copied.set(target);
    setTimeout(() => this.copied.set(null), 1500);

    const chatId = this.chatId();
    if (chatId !== null) {
      this.analytics
        .sendOutcomeCopiedEvent({
          chatId,
          kind: envelope.kind,
          format: envelope.format,
          target,
        })
        .subscribe();
    }
  }

  /**
   * SK-155: downloads this block as a file. The API builds it from the saved
   * edit, so a pending one is saved first.
   */
  protected async onExport(format: ExportFormat): Promise<void> {
    const { chatUuid, messageId, index } = this.target();
    if (chatUuid === null || messageId === null || this.exporting()) {
      return;
    }
    this.exporting.set(format);
    try {
      // The file is built from what the API holds: a save on its way lands
      // first, then a pending (or failed) edit is saved. If that save fails,
      // its toast has said so, and no older version is exported.
      clearTimeout(this.saveTimer);
      await this.writing;
      if (this.dirty && !(await this.save(this.target()))) {
        return;
      }
      const response = await firstValueFrom(
        format === 'docx'
          ? this.chatsService.chatsControllerExportOutcomeToDocxV1(
              chatUuid,
              messageId,
              index,
              'response',
            )
          : this.chatsService.chatsControllerExportOutcomeToPdfV1(
              chatUuid,
              messageId,
              index,
              'response',
            ),
      );
      if (response.body) {
        fromSeriousSystem.triggerDownload(
          response.body,
          getFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `export.${format}`,
          ),
        );
      }
      const message = String(
        this.translate.instant('CHAT.OUTCOME.TOAST.EXPORTED', {
          format: EXPORTS[format].name,
        }),
      );
      this.announcement.set(message);
      this.toastr.success(message);
      this.exported.set(format);
      setTimeout(() => this.exported.set(null), 2000);
    } catch (error) {
      const message = String(this.translate.instant(EXPORTS[format].errorKey));
      this.announcement.set(message);
      this.toastr.error(message, undefined, {
        closeButton: true,
        disableTimeOut: true,
      });
      if (reportsExportFailure(error)) {
        Sentry.captureException(error);
      }
    } finally {
      this.exporting.set(null);
    }
  }

  private scheduleSave(): void {
    this.dirty = true;
    clearTimeout(this.saveTimer);
    const target = this.target();
    this.saveTimer = setTimeout(() => void this.save(target), SAVE_DELAY_MS);
  }

  /**
   * Saves what the fields hold to `target`: the outcome they were filled for,
   * which may no longer be the one shown. Back to the original, it clears the
   * edit. Reads the fields themselves: a save that finished meanwhile may have
   * reset the signals behind what was typed since. True once the API holds it.
   */
  private async save(target: EditTarget): Promise<boolean> {
    this.dirty = false;
    const body =
      this.bodyField()?.nativeElement.value ?? this.richBody() ?? this.text();
    const subject =
      target.envelope.subject === undefined
        ? undefined
        : (this.subjectField()?.nativeElement.value ?? this.subject());
    const current = sameTarget(target, this.target());
    if (current) {
      this.text.set(body);
      if (subject !== undefined) {
        this.subject.set(subject);
      }
    }
    const edited =
      body !== target.body ||
      (subject !== undefined && subject !== target.envelope.subject);
    const saved = await this.write(edited ? { body, subject } : null, target);
    if (saved && edited && current && !this.editTracked) {
      this.editTracked = true;
      this.track('edited');
    }
    return saved;
  }

  /** PUT (`edit`) or DELETE (`null`), after the write before it; true once the API holds it. */
  private write(
    edit: { body: string; subject?: string | undefined } | null,
    target: EditTarget,
  ): Promise<boolean> {
    this.writing = this.writing.then(() => this.send(edit, target));
    return this.writing;
  }

  private async send(
    edit: { body: string; subject?: string | undefined } | null,
    target: EditTarget,
  ): Promise<boolean> {
    const { chatUuid, messageId, index } = target;
    if (chatUuid === null || messageId === null) {
      return true;
    }
    try {
      const { outcomeEdits } = await firstValueFrom(
        edit
          ? this.chatsService.chatsControllerSaveOutcomeEditV1(
              chatUuid,
              messageId,
              index,
              {
                body: edit.body,
                ...(edit.subject !== undefined && { subject: edit.subject }),
              },
            )
          : this.chatsService.chatsControllerRestoreOutcomeV1(
              chatUuid,
              messageId,
              index,
            ),
      );
      this.store.dispatch(
        ChatWithMessagesActions.outcomeEditsSaved({
          chatUuid,
          messageId,
          outcomeEdits: outcomeEdits ?? null,
        }),
      );
      return true;
    } catch {
      // The text stays as typed; the next pause or leaving the field retries
      // (only while that outcome is still the one shown).
      this.dirty = sameTarget(target, this.target());
      const message = String(
        this.translate.instant('CHAT.OUTCOME.SAVE_FAILED'),
      );
      this.announcement.set(message);
      this.toastr.error(message);
      return false;
    }
  }

  private track(action: 'edited' | 'restored'): void {
    const chatId = this.chatId();
    if (chatId === null) {
      return;
    }
    const event = {
      chatId,
      kind: this.envelope().kind,
      format: this.envelope().format,
    };
    (action === 'edited'
      ? this.analytics.sendOutcomeEditedEvent(event)
      : this.analytics.sendOutcomeRestoredEvent(event)
    ).subscribe();
  }

  /** "Message copied", "Translation copied", "Document copied"… by what was copied. */
  private toastKey(target: CopyTarget): string {
    const { subject, kind, format } = this.envelope();
    if (target === 'subject') {
      return 'CHAT.OUTCOME.TOAST.SUBJECT_COPIED';
    }
    if (subject) {
      return 'CHAT.OUTCOME.TOAST.MESSAGE_COPIED';
    }
    if (kind === 'translation') {
      return 'CHAT.OUTCOME.TOAST.TRANSLATION_COPIED';
    }
    return format === 'rich'
      ? 'CHAT.OUTCOME.TOAST.DOCUMENT_COPIED'
      : 'CHAT.OUTCOME.TOAST.TEXT_COPIED';
  }
}
